import type {
  GroundedOpenAction,
  GroundedProvenanceResult,
  GroundedWarning,
} from "../../api/groundedProvenance";

interface GroundedProvenanceAnswerProps {
  result: GroundedProvenanceResult;
  onDismiss: () => void;
  onOpenAction: (action: GroundedOpenAction) => void;
}

const STATUS_LABELS = {
  answered: "Grounded answer",
  incomplete: "Grounded answer · incomplete evidence",
  redacted: "Grounded answer · restricted evidence omitted",
  no_match: "No supported provenance question matched",
  ambiguous: "Grounded question needs clarification",
  needs_context: "Grounded question needs context",
} as const;

const WARNING_LABELS: Record<GroundedWarning["code"], string> = {
  proof_incomplete: "The authorized proof trace is incomplete.",
  impact_incomplete: "The authorized impact traversal is incomplete.",
  temporal_history_incomplete: "Temporal history is incomplete at this cut.",
  restricted_evidence_omitted: "Restricted evidence was omitted without leaking its contents or counts.",
  human_history_incomplete: "Authorized human-provenance history is incomplete.",
  citation_limit_reached: "The bounded citation limit was reached.",
  path_limit_reached: "The bounded path limit was reached.",
  no_authorized_human_evidence: "No authorized human evidence is visible at this cut.",
  human_evidence_page_truncated: "The authorized human-evidence page was truncated; conclusions are incomplete.",
};

function snapshotLabel(result: GroundedProvenanceResult): string | null {
  const snapshot = result.document.temporal_snapshot;
  if (!snapshot) return null;
  if (snapshot.snapshot_kind === "point") {
    return `Effective ${snapshot.effective_as_of} · recorded ${snapshot.recorded_as_of}`;
  }
  return `Effective window ${snapshot.before_as_of} → ${snapshot.after_as_of} · recorded ${snapshot.recorded_as_of}`;
}

export function GroundedProvenanceAnswer({
  result,
  onDismiss,
  onOpenAction,
}: GroundedProvenanceAnswerProps) {
  const { document } = result;
  const temporalLabel = snapshotLabel(result);

  return (
    <aside
      className={`lineage-workspace__grounded-answer is-${document.status}`}
      aria-label="Grounded provenance answer"
      aria-live="polite"
      data-answer-status={document.status}
    >
      <header>
        <div>
          <span>{STATUS_LABELS[document.status]}</span>
          <strong>{document.intent ? document.intent.split("_").join(" ") : "No evidence intent"}</strong>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss grounded provenance answer">×</button>
      </header>

      {temporalLabel ? <p className="lineage-workspace__grounded-cut">{temporalLabel}</p> : null}

      <ol className="lineage-workspace__grounded-statements" aria-label="Evidence-grounded statements">
        {document.statements.map((statement) => (
          <li key={statement.statement_id}>
            <p>{statement.text}</p>
            {statement.citation_ids.length ? (
              <small>{statement.citation_ids.length} authorized citation{statement.citation_ids.length === 1 ? "" : "s"}</small>
            ) : null}
          </li>
        ))}
      </ol>

      {document.requirements.length ? (
        <section className="lineage-workspace__grounded-requirements" aria-label="Context required">
          {document.requirements.map((requirement) => (
            <p key={requirement.code}>{requirement.message}</p>
          ))}
        </section>
      ) : null}

      {document.warnings.length ? (
        <ul className="lineage-workspace__grounded-warnings" aria-label="Evidence limitations">
          {document.warnings.map((warning, index) => <li key={`${warning.code}:${index}`}>{WARNING_LABELS[warning.code]}</li>)}
        </ul>
      ) : null}

      {document.open_actions.length ? (
        <div className="lineage-workspace__grounded-actions" aria-label="Open cited evidence">
          {document.open_actions.map((action) => (
            <button type="button" key={action.action_id} onClick={() => onOpenAction(action)}>
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {document.citations.length ? (
        <details className="lineage-workspace__grounded-citations">
          <summary>{document.citations.length} resolvable citation{document.citations.length === 1 ? "" : "s"}</summary>
          <ol aria-label="Authorized evidence citations">
            {document.citations.map((citation) => (
              <li key={citation.citation_id}>
                <span>{citation.source_tool.split("_").join(" ")} · {citation.record_kind}</span>
                <code>{citation.record_id}</code>
                <small>{citation.source_locator}</small>
              </li>
            ))}
          </ol>
        </details>
      ) : null}

      <footer>
        <span>{document.paths.length} explicit path{document.paths.length === 1 ? "" : "s"}</span>
        <code title={result.semantic_identity.digest}>{result.semantic_identity.digest.slice(0, 22)}…</code>
      </footer>
    </aside>
  );
}
